import { appStore } from '../../app/AppStore';
import { ServiceLocator } from '../../app/ServiceLocator';
import { workbookTypeOnly } from '../../app/spaces/ActionsMenu/EntityActions/IEntityActionHelpers';
import { currentUserIsAdmin } from '../../app/utils/MixedContentHelpers';
import { Formatters, Messages } from '../../codegen/Localize';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { ScheduleActions } from '../../schedule/types/ISchedule';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { WorkbookDefinition } from '../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IWorkbookBase, IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { IErrorItem } from '../../ts/models/MultipleItemResult';
import { Set } from '../../ts/models/Set';
import { Workbooks } from '../../ts/resources/Workbooks';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ActionHelpers } from '../../ts/services/ActionHelpers';
import { actionErrorTranslation, actionSuccessTranslation } from '../../ts/services/ContentActionNotification';
import { DataAccelerationErrors } from '../../ts/services/dataAcceleration/DataAccelerationErrors';
import { SearchSuggestionsService } from '../../ts/services/SearchSuggestionsService';
import { EnableAccelerationDialogBody, EnableAccelerationDialogBodyProps, EnableAccelerationDialogState } from '../components/EnableWorkbookAccelerationDialog';
import { AccelerationTasks } from '../resources/AccelerationTasks';
import { IAccelerationNeedsScheduleResult } from '../serverApi/requests/AccelerationNeedsScheduleRequest';


function confirmEnableAcceleration(content: IContentInfo[], needsSchedule: boolean): ng.IPromise<IResult> {
  const modalInstance = openReactActionDialog<EnableAccelerationDialogBodyProps, EnableAccelerationDialogState, IResult>({
    title:  Messages.accelerateWorkbooks(),
    bodyComponent: EnableAccelerationDialogBody,
    initialBodyProps: {
      prompt: getPromptForContent(needsSchedule),
      getQuery: () => getScheduleQuery(),
      needsSchedule: needsSchedule,
    },
    initialDialogState: { schedule: null },
    actionName: Messages.enableAcceleration(),
    action: (_bodyProps: EnableAccelerationDialogBodyProps, dialogState: EnableAccelerationDialogState) => {
      return enableAcceleration(content, dialogState.schedule).then((results: IResult[]) => {
        return ResultHelper.combineResults(results);
      });
    },
    actionDisabled: (_bodyProps: EnableAccelerationDialogBodyProps,
                      dialogState: EnableAccelerationDialogState) => needsSchedule && dialogState.schedule == null,
    dialogWidth: TabDialogWidth.width500px,
    dismissDialogOnActionFailed: false
  });
  return modalInstance.result;
}

function confirmDisableAcceleration(content: IContentInfo[]): ng.IPromise<IResult> {

  let dialogBody: string;
  const workbookCount = content.length;
  if(workbookCount === 1) {
    const workbook = content[0];
    dialogBody = Messages.dataAccelerationDisableWorkbookPrompt({
      WORKBOOK_NAME: L10n.elideIfLong(workbook.name)
    });
  } else {
    dialogBody = Messages.dataAccelerationDisableWorkbooksPrompt({
      ITEM_COUNT_LOC: Formatters.formatCount(workbookCount)
    });
  }

  const dialogInstance = openReactConfirmationDialog<IContentInfo[], IResult>({
    title: Messages.disableAcceleration(),
    message: dialogBody,
    actionName: Messages.disableAcceleration(),
    paramsForAction: content,
    isActionDelete: true,
    action: disableAcceleration,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

function getScheduleQuery() {
  const SearchSuggestionsService = ServiceLocator.get<SearchSuggestionsService>('SearchSuggestionsService');
  return SearchSuggestionsService.schedulesQuery(ScheduleActions.Data_Acceleration);
}

function enableAcceleration(content: IContentInfo[], schedule: IScheduleInfo) {
  const $q = ServiceLocator.get<ng.IQService>('$q');
  const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookDefinition.contentType));
  if (workbooks.size() < 1) {
    return $q.when(null);
  }
  const Workbooks = ServiceLocator.get<Workbooks>('Workbooks');

  return Workbooks.enableWorkbookAcceleration(workbooks.keys(), schedule===null ? undefined : schedule.id).then((result: IResult) => {
    return ActionHelpers.notifyStandardError(
      workbooks,
      result,
      {
        success: actionSuccessTranslation(Messages.accelerationTasksCreateSuccess),
        error: actionErrorTranslation(Messages.accelerationTasksCreateError),
      },
      (errorItem: IErrorItem<IWorkbookBase>) => DataAccelerationErrors.errorDetailsForWorkbooks(errorItem),
    );
  });
}

function disableAcceleration(content: IContentInfo[]) {
  const $q = ServiceLocator.get<ng.IQService>('$q');
  const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookDefinition.contentType));
  if (workbooks.size() < 1) {
    return $q.when(null);
  }
  const Workbooks = ServiceLocator.get<Workbooks>('Workbooks');

  return Workbooks.disableWorkbookAcceleration(workbooks.keys()).then((result: IResult) => {
    return ActionHelpers.notifyStandardError(
      workbooks,
      result,
      {
        success: actionSuccessTranslation(Messages.dataAccelerationDisableSuccess),
        error: actionErrorTranslation(Messages.dataAccelerationDisableError),
      },
      (errorItem: IErrorItem<IWorkbookBase>) => DataAccelerationErrors.errorDetailsForWorkbooks(errorItem),
    );
  });
}

function getPromptForContent(needsSchedule: boolean): string {
  if (needsSchedule) {
    return Messages.accelerationTasksWithSchedulePrompt();
  } else {
    return Messages.accelerationTasksNoSchedulePrompt();
  }
}

function accelerationNeedsSchedule(workbooks: IContentInfo[]): ng.IPromise<boolean> {
  const idArray = workbooks.map(c => c.id);
  return AccelerationTasks.needsSchedule(idArray).then((result: IAccelerationNeedsScheduleResult) => result.needsSchedule);
}

function checkPermission(targetItems: IWorkbookInfo[]): boolean {
  return FeatureFlags.workbookAccelerationUIEnabled &&
        currentUserIsAdmin(appStore.getState()) &&
        workbookTypeOnly(targetItems);
}

export const WorkbookAccelerationActions = {
  accelerationNeedsSchedule,
  enableAcceleration,
  disableAcceleration,
  confirmDisableAcceleration,
  confirmEnableAcceleration,
  assurePermission: checkPermission,
};

export type WorkbookAccelerationActions = typeof WorkbookAccelerationActions;
